import { useState } from "react";

import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ContestCharCountCell } from "@/components/contest-submissions/contest-char-count-cell";
import {
  useRecountCharCount,
  useSetScreening,
} from "@/hooks/use-contest-submissions";
import { deriveSuggestion } from "@/lib/contest-checklist";
import { cn } from "@/lib/utils";
import type {
  ContestChecklistValue,
  ContestScreeningResult,
  ContestSubmissionDetail,
} from "@/types/contest-submission";

import { formatDateTime } from "./format";
import { SubmissionChecklist } from "./submission-checklist";

interface Props {
  detail: ContestSubmissionDetail;
}

const VERDICT_BUTTONS: Array<{
  result: ContestScreeningResult;
  label: string;
  activeClass: string;
  suggestRing: string;
}> = [
  {
    result: "PASS",
    label: "적격",
    activeClass:
      "bg-emerald-600 text-white hover:bg-emerald-600/90 border-emerald-600",
    suggestRing: "ring-2 ring-emerald-500 ring-offset-1",
  },
  {
    result: "FAIL",
    label: "부적격",
    activeClass: "bg-rose-600 text-white hover:bg-rose-600/90 border-rose-600",
    suggestRing: "ring-2 ring-rose-500 ring-offset-1",
  },
  {
    result: "PENDING",
    label: "검토중",
    activeClass: "bg-gray-600 text-white hover:bg-gray-600/90 border-gray-600",
    suggestRing: "",
  },
];

const shallowEqualRecord = (
  a: Record<string, ContestChecklistValue>,
  b: Record<string, ContestChecklistValue>,
) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

/**
 * 1차 심사 패널 — 체크리스트(O/X/미정) + 판정 + 메모를 한 번의 mutation 으로 저장.
 * 체크리스트에서 판정을 자동 "제안"(ring 하이라이트)하되 자동 선택은 하지 않는다.
 *
 * 로컬 편집 상태는 마운트 시 서버 값으로 seed — 호출부의 key={detail._id}
 * 리마운트가 제출물 전환 시 리셋을 담당하고, 같은 제출물의 배경 refetch 는
 * 편집 중인 입력을 덮어쓰지 않는다 (드로어 시절 패턴 유지).
 */
export function SubmissionReviewPanel({ detail }: Props) {
  const setScreening = useSetScreening();
  const recount = useRecountCharCount();

  const savedResult = detail.screening?.result ?? "PENDING";
  const savedMemo = detail.screening?.memo ?? "";
  const savedChecklist: Record<string, ContestChecklistValue> =
    Object.fromEntries(
      (detail.screening?.checklist ?? []).map((item) => [item.key, item.value]),
    );

  const [result, setResult] = useState<ContestScreeningResult>(savedResult);
  const [memo, setMemo] = useState(savedMemo);
  const [checklist, setChecklist] =
    useState<Record<string, ContestChecklistValue>>(savedChecklist);

  const entries = Object.entries(checklist).map(([key, value]) => ({
    key,
    value,
  }));
  const suggestion = deriveSuggestion(entries);

  const isDirty =
    result !== savedResult ||
    memo.trim() !== savedMemo.trim() ||
    !shallowEqualRecord(checklist, savedChecklist);

  const handleChecklistChange = (key: string, value: ContestChecklistValue) => {
    setChecklist((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setScreening.mutate({
      submissionId: detail._id,
      result,
      memo: memo.trim() || undefined,
      // NA 도 포함해 full replace — 서버는 전달값 그대로 교체 저장.
      checklist: entries,
    });
  };

  const charCountHint =
    detail.charCount?.status === "DONE" && detail.charCount.value != null
      ? `${detail.charCount.value.toLocaleString("ko-KR")}자 자동 집계`
      : null;

  return (
    <div className="space-y-5 rounded-md border bg-white p-4">
      <div className="space-y-2">
        <h3 className="text-sm font-bold">형식 요건 체크리스트</h3>
        <SubmissionChecklist
          values={checklist}
          onChange={handleChecklistChange}
          charCountHint={charCountHint}
        />
      </div>

      <div className="space-y-2 border-t pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">판정</h3>
          {suggestion && suggestion !== result && (
            <span className="text-xs text-muted-foreground">
              체크리스트 기준{" "}
              {suggestion === "FAIL" ? "부적격" : "적격"} 제안
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {VERDICT_BUTTONS.map(({ result: value, label, activeClass, suggestRing }) => (
            <Button
              key={value}
              variant="outline"
              size="sm"
              className={cn(
                result === value && activeClass,
                // 제안된 판정(아직 미선택)에 ring 하이라이트 — 자동 선택은 하지 않는다.
                suggestion === value && result !== value && suggestRing,
              )}
              aria-pressed={result === value}
              onClick={() => setResult(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2 border-t pt-4">
        <h3 className="text-sm font-bold">심사 메모</h3>
        <Textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="심사 메모 (선택, 최대 2000자)"
          maxLength={2000}
          rows={4}
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <p className="text-xs text-muted-foreground">
          {detail.screening?.reviewedAt
            ? `${detail.screening.reviewerEmail ?? "-"} · ${formatDateTime(detail.screening.reviewedAt)} 판정`
            : "아직 판정 이력이 없습니다"}
        </p>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || setScreening.isPending}
        >
          {setScreening.isPending ? "저장 중…" : "심사 저장"}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-muted-foreground">글자수</span>
          <ContestCharCountCell charCount={detail.charCount} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => recount.mutate(detail._id)}
          disabled={recount.isPending}
          className="text-xs"
        >
          <RefreshCw className="mr-1 h-3 w-3" />
          {recount.isPending ? "요청 중…" : "재계산"}
        </Button>
      </div>
    </div>
  );
}
